import React, { useState, useEffect, useRef } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion, useLanguage } from '@hooks';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--text-accent); // 深绿色
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h2 {
    color: var(--text-primary); // 白色
  }

  h3 {
    margin-top: 5px;
    color: var(--text-primary); // 白色
    line-height: 0.9;
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
  }
`;

/**
 * Hero组件 - 首页主要展示区域
 * 包含动画效果的文本展示
 */
const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const { t } = useLanguage();

  // 为每个动画元素创建ref
  const nodeRefs = useRef([]);

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);
    return () => clearTimeout(timeout);
  }, []);

  const one = <h1>{t('greeting')}</h1>;
  const two = <h2 className="big-heading">{t('name')}</h2>;
  const three = <h3 className="big-heading">{t('tagline')}</h3>;
  const four = (
    <>
      <p>
        {t('description')}{' '}
        <a href="https://github.com/Id8fun" target="_blank" rel="noreferrer">
          {t('Github')}
        </a>
        .
      </p>
    </>
  );
  const five = (
    <a className="email-link" href="/application" rel="noreferrer">
      {t('cta')}
    </a>
  );

  const items = [one, two, three, four, five];

  // 确保nodeRefs数组有足够的ref
  if (nodeRefs.current.length !== items.length) {
    nodeRefs.current = Array(items.length)
      .fill()
      .map((_, i) => nodeRefs.current[i] || React.createRef());
  }

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <>
          {items.map((item, i) => (
            <div key={i}>{item}</div>
          ))}
        </>
      ) : (
        <TransitionGroup component={null}>
          {isMounted &&
            items.map((item, i) => (
              <CSSTransition
                key={i}
                classNames="fadeup"
                timeout={loaderDelay}
                nodeRef={nodeRefs.current[i]}>
                <div ref={nodeRefs.current[i]} style={{ transitionDelay: `${i + 1}00ms` }}>
                  {item}
                </div>
              </CSSTransition>
            ))}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
